import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, Observable, EMPTY, throwError, combineLatest } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  withLatestFrom,
  filter,
  pairwise
} from 'rxjs/operators';
import {
  MonitorActionTypes,
  LoadMonitorSuccess,
  LoadMonitorFail,
  RemoveMonitorDeals,
  TaintMonitor,
  LoadMonitor,
  SaveMonitorDealScoreSuccess,
  SaveMonitorDealScoreFail,
  SaveMonitorDealScore,
  SaveMonitorDealStatusSuccess,
  SaveMonitorDealStatusFail,
  SaveMonitorDealStatus,
  SaveMonitorDealOwnershipFail,
  SaveMonitorDealOwnershipSuccess,
  SaveMonitorDealOwnership,
  SaveMonitorDealRatingSuccess,
  SaveMonitorDealRatingFail,
  SaveMonitorDealRating
} from './actions';
import {
  FollowsActionTypes,
  Unfollow,
  UnfollowMultiple,
  GroupUnfollowUpdate,
  GroupFollowUpdate
} from '../follows/actions';
import { MonitorService } from '../../services/monitor.service';
import { DealVersion } from '../../types/deal.types';
import { Store, select } from '@ngrx/store';
import {
  AssignmentsActionTypes,
  AssignUsersSuccess
} from '../assignments/actions';
import {
  State,
  getDealFollows,
  getDealGroupFollows,
  selectMonitorStatusState,
  selectMonitorDeals,
  selectIpreoAccountUserId,
  selectMonitorMetrics
} from '../reducer';
import { DealMetricsState } from '../deal-metrics/state';
import { MonitorMetrics } from './state';
import { DealMetricsService } from '../../services/deal-metrics.service';
import { NotifyService } from '../../services/notify.service';
import { UsersFacade } from '../../facades/users.facade';
import { User } from '../../types/users.types';

@Injectable()
export class MonitorEffects {
  
  public loadDeals$: Observable<
    LoadMonitorSuccess | LoadMonitorFail
  > = createEffect(() => this.actions$.pipe(
    ofType(MonitorActionTypes.LoadMonitor),
    switchMap(() =>
      combineLatest([
        this.monitorService.getDeals(),
        this.usersFacade.usersState$.pipe(
          filter(({ loaded }) => !!loaded),
          map(({ users }) => users),
          catchError((error) => throwError(error))
        )
      ]).pipe(
        map(([deals, users]: [DealVersion[], User[]]) =>
          deals.map((deal) => ({ ...deal, users }))
        ),
        map((deals) => new LoadMonitorSuccess({ data: deals })),
        catchError((error) =>
          of(new LoadMonitorFail(error && (error as Error).message))
        )
      )
    )
  ));

  
  public unfollowDeals$: Observable<RemoveMonitorDeals> = createEffect(() => this.actions$.pipe(
    ofType(FollowsActionTypes.Unfollow, FollowsActionTypes.UnfollowMultiple),
    map((action: Unfollow | UnfollowMultiple) =>
      action.payload instanceof Array ? action.payload : [action.payload]
    ),
    withLatestFrom(
      this.store.pipe(select(getDealGroupFollows)),
      this.store.pipe(selectIpreoAccountUserId),
      this.store.pipe(select(selectMonitorDeals))
    ),
    map(
      ([unfollowedDealIds, dealGroupFollows, userId, deals]: [
        string[],
        string[],
        string,
        DealVersion[]
      ]) => {
        const shouldBeRemovedFromMonitor = [];
        for (const dealId of unfollowedDealIds) {
          const deal = (deals || []).find(({ id }) => id === dealId);
          const dealGroupFollowed = dealGroupFollows.indexOf(dealId) > -1;
          const dealIsAssignedToMe =
            deal && (deal.assignedUsers || []).indexOf(userId) > -1;
          if (!(dealGroupFollowed || dealIsAssignedToMe)) {
            shouldBeRemovedFromMonitor.push(dealId);
          }
        }
        return new RemoveMonitorDeals(shouldBeRemovedFromMonitor);
      }
    )
  ));

  
  public followDeals$: Observable<TaintMonitor> = createEffect(() => this.actions$.pipe(
    ofType(FollowsActionTypes.Follow, FollowsActionTypes.FollowMultiple),
    map(() => new TaintMonitor())
  ));

  
  public groupFollowedDeal$: Observable<
    LoadMonitor | never
  > = createEffect(() => this.actions$.pipe(
    ofType(FollowsActionTypes.GroupFollowUpdate),
    withLatestFrom(
      this.store.pipe(select(selectMonitorStatusState)),
      this.store.pipe(select(selectMonitorDeals)),
      this.store.pipe(select(getDealFollows))
    ),
    filter(([, monitorStatus]) => monitorStatus),
    switchMap(
      ([action, , monitorDeals, followedDealIds]: [
        GroupFollowUpdate,
        boolean,
        DealVersion[],
        string[]
      ]) => {
        const dealId = action.payload;
        const dealInMonitor =
          monitorDeals.map((deal) => deal.id).indexOf(dealId) > -1;
        const dealIsPrivateFollowed = !!followedDealIds.filter(
          (deal) => deal === dealId
        ).length;
        if (!dealInMonitor || dealIsPrivateFollowed) {
          return of(new LoadMonitor(false));
        } else {
          return EMPTY;
        }
      }
    )
  ));

  
  public groupUnfollowedDeal$: Observable<
    RemoveMonitorDeals | never
  > = createEffect(() => this.actions$.pipe(
    ofType(FollowsActionTypes.GroupUnfollowUpdate),
    withLatestFrom(
      this.store.pipe(select(getDealFollows)),
      this.store.pipe(selectIpreoAccountUserId),
      this.store.pipe(select(selectMonitorDeals))
    ),
    switchMap(
      ([action, dealFollows, userId, deals]: [
        GroupUnfollowUpdate,
        string[],
        string,
        DealVersion[]
      ]) => {
        const dealId = action.payload;
        const deal = (deals || []).find(({ id }) => id === dealId);
        const dealIndividuallyFollowed = dealFollows.indexOf(dealId) > -1;
        const dealIsAssignedToMe =
          deal && (deal.assignedUsers || []).indexOf(userId) > -1;
        if (!(dealIndividuallyFollowed || dealIsAssignedToMe)) {
          return of(new RemoveMonitorDeals([dealId]));
        } else {
          return EMPTY;
        }
      }
    )
  ));

  
  public readonly assignmentsChanged$: Observable<
    RemoveMonitorDeals | LoadMonitor | never
  > = createEffect(() => this.actions$.pipe(
    ofType(AssignmentsActionTypes.AssignUsersSuccess),
    withLatestFrom(
      this.store.pipe(selectIpreoAccountUserId),
      this.store.pipe(select(getDealFollows)),
      this.store.pipe(select(getDealGroupFollows)),
      this.store.pipe(select(selectMonitorDeals))
    ),
    switchMap(
      ([action, userId, dealFollows, groupDealFollows, monitorDeals]: [
        AssignUsersSuccess,
        string,
        string[],
        string[],
        DealVersion[]
      ]) => {
        const assignedUsers = action.payload.userIds;
        const dealId = action.payload.dealId;

        const dealWasUnassigned = assignedUsers.indexOf(userId) === -1;
        const dealIsFollowed = dealFollows.indexOf(dealId) > -1;
        const dealIsGroupFollowed = groupDealFollows.indexOf(dealId) > -1;

        if (dealWasUnassigned && !(dealIsFollowed || dealIsGroupFollowed)) {
          // Deal has been unassigned to me.
          // Remove it from the DM if it is not followed or group followed.
          return of(new RemoveMonitorDeals([dealId]));
        } else {
          const dealNotInMonitor =
            (monitorDeals || []).map((deal) => deal.id).indexOf(dealId) === -1;
          const dealWasAssignedToMe = assignedUsers.indexOf(userId) > -1;

          if (dealNotInMonitor && dealWasAssignedToMe) {
            // Need to reload the DM if a new deal was assigned to me.
            return of(new LoadMonitor(false));
          } else {
            // Deal might be in the monitor, we'll just update it in
            // the store with the new list of asasigned users.
            return EMPTY;
          }
        }
      }
    )
  ));

  
  public saveScore$: Observable<
    SaveMonitorDealScoreSuccess | SaveMonitorDealScoreFail
  > = createEffect(() => this.actions$.pipe(
    ofType(MonitorActionTypes.SaveMonitorDealScore),
    withLatestFrom(this.store.pipe(select(selectMonitorMetrics), pairwise())),
    switchMap(
      ([
        {
          payload: { dealId, score }
        },
        [prevScore]
      ]: [
        SaveMonitorDealScore,
        [
          MonitorMetrics<Partial<DealMetricsState>>,
          MonitorMetrics<Partial<DealMetricsState>>
        ]
      ]) =>
        this.dealMetricsService.saveScore(dealId, score).pipe(
          map(() => new SaveMonitorDealScoreSuccess({ dealId, score })),
          catchError((err) => {
            this.notifyService.createMsg(
              `Failed to save Internal Notes: '${score}', previous value restored`
            );
            return of(
              new SaveMonitorDealScoreFail({
                dealId,
                score: prevScore[dealId]?.score,
                error: err
              })
            );
          })
        )
    )
  ));

  
  public saveRating$: Observable<
    SaveMonitorDealRatingSuccess | SaveMonitorDealRatingFail
  > = createEffect(() => this.actions$.pipe(
    ofType(MonitorActionTypes.SaveMonitorDealRating),
    withLatestFrom(this.store.pipe(select(selectMonitorMetrics), pairwise())),
    switchMap(
      ([
        {
          payload: { dealId, rating }
        },
        [prevRating]
      ]: [
        SaveMonitorDealRating,
        [
          MonitorMetrics<Partial<DealMetricsState>>,
          MonitorMetrics<Partial<DealMetricsState>>
        ]
      ]) =>
        this.dealMetricsService.saveRating(dealId, rating).pipe(
          map(() => new SaveMonitorDealRatingSuccess({ dealId, rating })),
          catchError((err) => {
            this.notifyService.createMsg(
              `Failed to save Internal Rating: '${rating}', previous value restored`
            );
            return of(
              new SaveMonitorDealRatingFail({
                dealId,
                rating: prevRating[dealId]?.rating,
                error: err
              })
            );
          })
        )
    )
  ));

  
  public saveOwnership$: Observable<
    SaveMonitorDealOwnershipSuccess | SaveMonitorDealOwnershipFail
  > = createEffect(() => this.actions$.pipe(
    ofType(MonitorActionTypes.SaveMonitorDealOwnership),
    withLatestFrom(this.store.pipe(select(selectMonitorMetrics), pairwise())),
    switchMap(
      ([
        {
          payload: { dealId, ownership }
        },
        [prevOwnership]
      ]: [
        SaveMonitorDealOwnership,
        [
          MonitorMetrics<Partial<DealMetricsState>>,
          MonitorMetrics<Partial<DealMetricsState>>
        ]
      ]) =>
        this.dealMetricsService.saveOwnership(dealId, ownership).pipe(
          map(() => new SaveMonitorDealOwnershipSuccess({ dealId, ownership })),
          catchError((err) => {
            this.notifyService.createMsg(
              `Failed to save Ownership: '${ownership}', previous value restored`
            );
            return of(
              new SaveMonitorDealOwnershipFail({
                dealId,
                ownership: prevOwnership[dealId]?.ownership,
                error: err
              })
            );
          })
        )
    )
  ));

  
  public saveStatus$: Observable<
    SaveMonitorDealStatusSuccess | SaveMonitorDealStatusFail
  > = createEffect(() => this.actions$.pipe(
    ofType(MonitorActionTypes.SaveMonitorDealStatus),
    withLatestFrom(this.store.pipe(select(selectMonitorMetrics), pairwise())),
    switchMap(
      ([
        {
          payload: { dealId, status }
        },
        [prevStatus]
      ]: [
        SaveMonitorDealStatus,
        [
          MonitorMetrics<Partial<DealMetricsState>>,
          MonitorMetrics<Partial<DealMetricsState>>
        ]
      ]) =>
        this.dealMetricsService.saveStatus(dealId, status).pipe(
          map(() => new SaveMonitorDealStatusSuccess({ dealId, status })),
          catchError((err) => {
            this.notifyService.createMsg(
              `Failed to save Internal Status: '${status}', previous value restored`
            );
            return of(
              new SaveMonitorDealStatusFail({
                dealId,
                status: prevStatus[dealId]?.status,
                error: err
              })
            );
          })
        )
    )
  ));

  constructor(
    public monitorService: MonitorService,
    private actions$: Actions,
    private store: Store<State>,
    private dealMetricsService: DealMetricsService,
    private readonly notifyService: NotifyService,
    public usersFacade: UsersFacade
  ) {}
}
